const API_URL = process.env.REACT_APP_API_URL || '';

// Simple API call function
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_URL}${endpoint}`;

  // Get auth token for protected endpoints
  const token = localStorage.getItem('novamatch_auth_token');
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    headers,
    ...options,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return response.json();
};

class ScanHistoryService {
  // Get all scan history for the current user's account
  async getScanHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add sorting
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      // Add filters
      if (params.status) queryParams.append('status', params.status);
      if (params.minScore) queryParams.append('minScore', params.minScore);
      if (params.maxScore) queryParams.append('maxScore', params.maxScore);
      if (params.jobTitle) queryParams.append('jobTitle', params.jobTitle);
      if (params.company) queryParams.append('company', params.company);
      if (params.tags) {
        const tagArray = Array.isArray(params.tags) ? params.tags : [params.tags];
        tagArray.forEach(tag => queryParams.append('tags', tag));
      }
      if (params.search) queryParams.append('search', params.search);
      
      return await apiCall(`/api/scan-history?${queryParams.toString()}`);
    } catch (error) {
      throw error;
    }
  }

  // Get a specific scan by ID
  async getScanById(scanId) {
    try {
      return await apiCall(`/api/scan-history/${scanId}`);
    } catch (error) {
      throw error;
    }
  }

  // Get recent scans for dashboard
  async getRecentScans(limit = 5) {
    try {
      return await apiCall(`/api/scan-history/recent/dashboard?limit=${limit}`);
    } catch (error) {
      throw error;
    }
  }

  // Get scan statistics
  async getScanStatistics(period = '30d') {
    try {
      return await apiCall(`/api/scan-history/stats/overview?period=${period}`);
    } catch (error) {
      throw error;
    }
  }

  // Update scan metadata
  async updateScan(scanId, updateData) {
    try {
      return await apiCall(`/api/scan-history/${scanId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
    } catch (error) {
      throw error;
    }
  }

  // Archive a scan
  async archiveScan(scanId) {
    try {
      return await apiCall(`/api/scan-history/${scanId}/archive`, {
        method: 'PATCH'
      });
    } catch (error) {
      throw error;
    }
  }

  // Unarchive a scan
  async unarchiveScan(scanId) {
    try {
      return await apiCall(`/api/scan-history/${scanId}/unarchive`, {
        method: 'PATCH'
      });
    } catch (error) {
      throw error;
    }
  }

  // Delete a scan (soft delete)
  async deleteScan(scanId) {
    try {
      return await apiCall(`/api/scan-history/${scanId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      throw error;
    }
  }

  // Delete scan history entry (alias for deleteScan)
  async deleteScanHistory(scanId) {
    return this.deleteScan(scanId);
  }

  // Format scan data for display
  formatScanForDisplay(scan) {
    if (!scan) return null;

    const scores = scan.analysisResults?.scores;
    
    // Use raw database scores without any corrections
    const overallScore = scores?.overall?.score || 0;
    const verdict = scores?.overall?.verdict || 'UNKNOWN';
    
    const company = scan.inputData?.companyName || scan.inputData?.company || '';
    
    return {
      id: scan.scanId,
      scanName: scan.scanName,
      jobTitle: scan.inputData?.jobTitle || 'Unknown Position',
      company: company,
      jobLevel: scan.inputData?.jobLevel || 'unknown',
      jobType: scan.inputData?.jobType || 'full-time',
      overallScore,
      verdict,
      candidateName: scan.analysisResults?.candidateMetadata?.name || 'Unknown Candidate',
      createdAt: scan.createdAt,
      completedAt: scan.completedAt,
      processingTime: scan.processingMetadata?.processingTime || 0,
      status: scan.status,
      isArchived: scan.isArchived,
      tags: scan.tags || [],
      notes: scan.notes || '',
      // Quick preview data with raw database scores
      preview: (() => {
        const mustHaveSkills = scan.analysisResults?.keywords?.jdMustHave?.length || 0;
        // Support both old (mustHaveCovered) and new (normalized) field names for backward compatibility
        const coveredSkills = (scan.analysisResults?.keywordMatches?.mustHaveCovered?.length || 
                               scan.analysisResults?.keywordMatches?.criticalMatchedSkills?.length || 0);
        // Calculate coverage percent based on actual counts for accuracy
        const coveragePercent = mustHaveSkills > 0 ? Math.round((coveredSkills / mustHaveSkills) * 100) : 0;
        
        return {
          experienceScore: scores?.experienceIndustryFit?.score || 0,
          skillsScore: scores?.technicalSkillsCerts?.score || 0,
          educationScore: scores?.educationCredentials?.score || 0,
          communicationScore: scores?.communicationSoftSkills?.score || 0,
          achievementScore: scores?.achievementQuality?.score || 0,
          riskFlags: scan.analysisResults?.riskFlags?.length || 0,
          mustHaveSkills,
          coveredSkills,
          coveragePercent
        };
      })()
    };
  }

  // Format multiple scans for display
  formatScansForDisplay(scans) {
    if (!Array.isArray(scans)) return [];
    return scans.map(scan => this.formatScanForDisplay(scan));
  }

  // Get score color based on score value
  getScoreColor(score) {
    if (score >= 80) return '#4caf50'; // Green
    if (score >= 60) return '#ff9800'; // Orange
    if (score >= 40) return '#ff5722'; // Red
    return '#f44336'; // Dark red
  }

  // Get verdict color based on verdict
  getVerdictColor(verdict) {
    switch (verdict) {
      case 'EXCELLENT FIT': return '#4caf50';
      case 'STRONG FIT': return '#66bb6a';
      case 'QUALIFIED': return '#ff9800';
      case 'POTENTIAL': return '#ff5722';
      case 'NOT ALIGNED': return '#f44336';
      // Legacy support
      case 'MODERATE FIT': return '#ff9800';
      case 'WEAK FIT': return '#ff5722';
      case 'POOR FIT': return '#f44336';
      default: return '#9e9e9e';
    }
  }

  // Format date for display
  formatDate(dateString) {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Format relative time
  formatRelativeTime(dateString) {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  }
}

const scanHistoryService = new ScanHistoryService();
export default scanHistoryService;
